/**
 * Urgent Workorders View - List/Deck/Kanban view for urgent workorders
 *
 * Features:
 * - View toggle: List (DataTable), Deck (Cards), Kanban (by status)
 * - Filters: Status, Tech
 * - DataTable with draggable/resizable columns, infinite scroll
 * - Urgent filter: urgent === true (synced as 'U' -> boolean)
 */

import { useState, useMemo, useCallback, useRef, useEffect, memo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { DataTable } from '../ui/DataTable';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { type WorkOrder } from '../../lib/api';
import { auth } from '../../lib/firebase';
import {
  createWorkOrderColumns,
  WORKORDER_SEARCHABLE_COLUMNS,
  type WorkOrderRow,
} from './columns';

// Same base as lib/api.ts: the API is a separate service, never the web origin.
const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3001').trim();

type ViewMode = 'list' | 'deck' | 'kanban';

const PAGE_SIZE = 200;

// Status groups for kanban
const KANBAN_COLUMNS = [
  { key: '0', label: 'New', color: 'amber' },
  { key: '1', label: 'Processing', color: 'blue' },
  { key: '2', label: 'Ordered', color: 'sage' },
  { key: '3', label: 'Received', color: 'sage' },
  { key: '4', label: 'Ready', color: 'green' },
];

interface UrgentWorkordersViewProps {
  onSelect?: (workorder: WorkOrder) => void;
}

// =============================================================================
// HOOKS
// =============================================================================

interface UrgentWorkOrdersPage {
  data: WorkOrder[];
  meta: { total: number; limit: number; offset: number };
  nextOffset: number | undefined;
}

function useUrgentWorkOrdersInfinite(statusFilter?: string, techFilter?: string) {
  return useInfiniteQuery({
    queryKey: ['workorders', 'urgent', 'infinite', { statusFilter, techFilter }],
    queryFn: async ({ pageParam = 0 }): Promise<UrgentWorkOrdersPage> => {
      const params = new URLSearchParams();
      params.set('open', 'true');
      params.set('urgent', 'true');
      if (statusFilter) params.set('status', statusFilter);
      if (techFilter) params.set('assignedTo', techFilter);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(pageParam));

      const query = `?${params.toString()}`;
      const token = await auth.currentUser?.getIdToken();
      const response = await fetch(`${API_BASE}/api/v1/workorders${query}`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch urgent workorders');
      }

      const result = await response.json();
      const data = (result.data as WorkOrder[]).filter(wo => wo.urgent === true);
      const meta = result.meta || { total: data.length, limit: PAGE_SIZE, offset: pageParam };
      const currentEnd = pageParam + data.length;
      const hasMore = data.length === PAGE_SIZE && currentEnd < meta.total;

      return { data, meta, nextOffset: hasMore ? currentEnd : undefined };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

// =============================================================================
// HELPERS
// =============================================================================

function formatAge(days: number | undefined): string {
  if (days === undefined || days === null) return '-';
  if (days === 0) return 'Today';
  if (days === 1) return '1 day';
  if (days < 7) return `${days} days`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}

function getStatusLabel(status: string): string {
  // Extract the label after the number prefix (e.g., "1. Contact Made" → "Contact Made")
  const match = status.match(/^\d+\.\s*(.+)$/);
  return match ? match[1] : status;
}

function getStatusVariant(status: string): 'default' | 'success' | 'warning' | 'danger' | 'info' | 'earth' {
  if (status.includes('Pending')) return 'warning';
  if (status.includes('Progress') || status.includes('Assigned')) return 'info';
  if (status.includes('Complete') || status.includes('Ready')) return 'success';
  if (status.includes('Declined')) return 'danger';
  return 'default';
}

// =============================================================================
// COMPONENT
// =============================================================================

export function UrgentWorkordersView({ onSelect }: UrgentWorkordersViewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedWO, setSelectedWO] = useState<WorkOrder | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [techFilter, setTechFilter] = useState<string>('');
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Fetch data with infinite scroll
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = useUrgentWorkOrdersInfinite(statusFilter, techFilter);

  // Set up intersection observer for infinite scroll (list/deck views)
  useEffect(() => {
    if (viewMode === 'kanban') return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, viewMode]);

  // Flatten all pages into a single array
  const allWorkorders = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.data);
  }, [data?.pages]);

  // Transform to rows for DataTable
  const rows: WorkOrderRow[] = useMemo(() => {
    return allWorkorders.map((wo) => ({
      id: wo.id,
      status: wo.status,
      urgent: wo.urgent,
      clientName: wo.clientName,
      clientId: wo.clientId,
      itemName: wo.itemName,
      qty: wo.qty,
      assignedTo: wo.assignedTo,
      assignedToName: wo.assignedToName,
      woRun: wo.woRun,
      submittedBy: wo.submittedBy,
      submittedByName: wo.submittedByName,
      notes: wo.notes,
      location: wo.location,
      billable: wo.billable,
      floral: wo.floral,
      deadline: wo.deadline,
      timestamp: wo.timestamp,
      createdAt: wo.createdAt,
      ageDays: wo.ageDays,
      detailedDescription: wo.detailedDescription,
      stage: wo.stage,
    }));
  }, [allWorkorders]);

  // Get unique techs for filter dropdown
  const uniqueTechs = useMemo(() => {
    const techs = new Set<string>();
    allWorkorders.forEach(wo => {
      if (wo.assignedToName) techs.add(wo.assignedToName);
    });
    return Array.from(techs).sort();
  }, [allWorkorders]);

  // Create columns
  const columns = useMemo(() => createWorkOrderColumns({ canSeeBilling: false }), []);

  // Handle row click
  const handleRowClick = useCallback(
    (row: WorkOrderRow) => {
      const wo = allWorkorders.find((w) => w.id === row.id);
      if (wo) {
        setSelectedWO(wo);
        onSelect?.(wo);
      }
    },
    [onSelect, allWorkorders]
  );

  const handleSelect = (wo: WorkOrder) => {
    setSelectedWO(wo);
    onSelect?.(wo);
  };

  // Get total count
  const totalCount = data?.pages[0]?.meta?.total ?? rows.length;
  const loadedCount = rows.length;

  if (error) {
    return (
      <div className="text-center py-12 text-red-600">
        Failed to load urgent workorders
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex-shrink-0 px-4 sm:px-6 py-4 border-b border-sage-200 bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔥</span>
            <div>
              <h1 className="text-xl font-display font-semibold text-forest-900">Urgent Workorders</h1>
              <p className="text-sm text-sage-600">
                {loadedCount} of {totalCount} urgent items
              </p>
            </div>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-3">
            <div className="flex rounded-xl border border-sage-300 overflow-hidden">
              {(['list', 'deck', 'kanban'] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors capitalize ${
                    viewMode === mode
                      ? 'bg-earth-500 text-white'
                      : 'bg-white text-sage-600 hover:bg-sage-50'
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mt-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-sage-300 text-sm focus:border-earth-500 focus:ring-2 focus:ring-earth-500/20 outline-none"
          >
            <option value="">All Statuses</option>
            <option value="0.">New</option>
            <option value="1.">Processing</option>
            <option value="2.">Ordered</option>
            <option value="3.">Received</option>
            <option value="4.">Ready</option>
          </select>

          <select
            value={techFilter}
            onChange={(e) => setTechFilter(e.target.value)}
            className="px-3 py-2 rounded-xl border border-sage-300 text-sm focus:border-earth-500 focus:ring-2 focus:ring-earth-500/20 outline-none"
          >
            <option value="">All Techs</option>
            {uniqueTechs.map(tech => (
              <option key={tech} value={tech}>{tech}</option>
            ))}
          </select>

          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setStatusFilter('');
              setTechFilter('');
            }}
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-earth-500 border-t-transparent rounded-full" />
          </div>
        ) : allWorkorders.length === 0 ? (
          <div className="text-center py-12 text-sage-500">
            <div className="text-4xl mb-4">✅</div>
            <p className="font-medium">No urgent workorders</p>
            <p className="text-sm mt-1">All urgent items have been addressed</p>
          </div>
        ) : viewMode === 'list' ? (
          <div className="h-full flex flex-col">
            <div className="flex-1 overflow-auto p-4">
              <DataTable
                tableId="urgent-workorders-table"
                data={rows}
                columns={columns}
                loading={isLoading}
                emptyMessage="No urgent workorders found"
                searchPlaceholder="Search urgent workorders..."
                searchableColumns={WORKORDER_SEARCHABLE_COLUMNS}
                onRowClick={handleRowClick}
                showRefresh
                onRefresh={() => refetch()}
              />
            </div>
            {/* Infinite scroll trigger */}
            <div ref={loadMoreRef} className="py-4 flex justify-center">
              {isFetchingNextPage && (
                <div className="flex items-center gap-2 text-sage-500">
                  <div className="animate-spin h-4 w-4 border-2 border-earth-500 border-t-transparent rounded-full" />
                  <span className="text-sm">Loading more...</span>
                </div>
              )}
            </div>
          </div>
        ) : viewMode === 'deck' ? (
          <div className="h-full overflow-auto p-4">
            <div className="space-y-3">
              {allWorkorders.map((wo) => (
                <UrgentWorkorderCard
                  key={wo.id}
                  workorder={wo}
                  onClick={() => handleSelect(wo)}
                  isSelected={selectedWO?.id === wo.id}
                />
              ))}
            </div>
            {/* Infinite scroll trigger */}
            <div ref={loadMoreRef} className="py-4 flex justify-center">
              {isFetchingNextPage && (
                <div className="flex items-center gap-2 text-sage-500">
                  <div className="animate-spin h-4 w-4 border-2 border-earth-500 border-t-transparent rounded-full" />
                  <span className="text-sm">Loading more...</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Kanban View */
          <div className="h-full overflow-x-auto p-4">
            <div className="flex gap-4 min-w-max h-full">
              {KANBAN_COLUMNS.map((col) => {
                const columnWOs = allWorkorders.filter(wo => wo.status.startsWith(`${col.key}.`));
                return (
                  <div
                    key={col.key}
                    className="w-80 flex-shrink-0 bg-sage-50 rounded-xl p-3 flex flex-col"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-forest-900">{col.label}</h3>
                      <Badge variant={col.color as 'default'} size="sm">{columnWOs.length}</Badge>
                    </div>
                    <div className="flex-1 overflow-y-auto space-y-2">
                      {columnWOs.map((wo) => (
                        <KanbanCard
                          key={wo.id}
                          workorder={wo}
                          onClick={() => handleSelect(wo)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Detail Panel (for deck view) */}
      {selectedWO && viewMode === 'deck' && (
        <DetailPanel workorder={selectedWO} onClose={() => setSelectedWO(null)} />
      )}
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface UrgentWorkorderCardProps {
  workorder: WorkOrder;
  onClick: () => void;
  isSelected?: boolean;
}

const UrgentWorkorderCard = memo(({ workorder, onClick, isSelected }: UrgentWorkorderCardProps) => {
  return (
    <Card
      className={`cursor-pointer transition-all ${
        isSelected
          ? 'ring-2 ring-earth-500 border-earth-500'
          : 'hover:shadow-md hover:border-sage-300'
      }`}
      onClick={onClick}
    >
      <div className="p-4">
        <div className="flex gap-4">
          <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-earth-100 flex items-center justify-center">
            <span className="text-2xl">🔥</span>
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-semibold text-forest-900 truncate">
                {workorder.detailedDescription || `${workorder.qty} ${workorder.itemName}`}
              </h3>
              <Badge variant="earth" size="sm">URGENT</Badge>
            </div>
            <p className="text-sm text-sage-600 truncate">{workorder.clientName}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-sage-500">
              {workorder.assignedToName && <span>Tech: {workorder.assignedToName}</span>}
              <span>•</span>
              <span>{getStatusLabel(workorder.status)}</span>
              <span>•</span>
              <span>{formatAge(workorder.ageDays)} old</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
});

UrgentWorkorderCard.displayName = 'UrgentWorkorderCard';

interface KanbanCardProps {
  workorder: WorkOrder;
  onClick: () => void;
}

const KanbanCard = memo(({ workorder, onClick }: KanbanCardProps) => {
  return (
    <Card
      className="cursor-pointer hover:shadow-md transition-shadow"
      onClick={onClick}
    >
      <div className="p-3">
        <div className="flex items-start gap-2">
          <span className="text-lg">🔥</span>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm text-forest-900 truncate">
              {workorder.detailedDescription || `${workorder.qty} ${workorder.itemName}`}
            </h4>
            <p className="text-xs text-sage-600 truncate mt-0.5">{workorder.clientName}</p>
            <div className="flex items-center gap-2 mt-2 text-xs text-sage-500">
              {workorder.assignedToName && (
                <span className="truncate">{workorder.assignedToName}</span>
              )}
              <span>•</span>
              <span>{formatAge(workorder.ageDays)}</span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
});

KanbanCard.displayName = 'KanbanCard';

interface DetailPanelProps {
  workorder: WorkOrder;
  onClose: () => void;
}

function DetailPanel({ workorder, onClose }: DetailPanelProps) {
  return (
    <Card className="mx-4 mb-4 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-forest-900">
          {workorder.detailedDescription || `WO #${workorder.id}`}
        </h3>
        <button onClick={onClose} className="text-sage-400 hover:text-sage-600">✕</button>
      </div>
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-sage-500">Client</dt>
          <dd className="text-forest-900">{workorder.clientName || '-'}</dd>
        </div>
        <div>
          <dt className="text-sage-500">Status</dt>
          <dd><Badge variant={getStatusVariant(workorder.status)}>{getStatusLabel(workorder.status)}</Badge></dd>
        </div>
        <div>
          <dt className="text-sage-500">Tech</dt>
          <dd className="text-forest-900">{workorder.assignedToName || '-'}</dd>
        </div>
        <div>
          <dt className="text-sage-500">Age</dt>
          <dd className="text-forest-900">{formatAge(workorder.ageDays)}</dd>
        </div>
        {workorder.location && (
          <div className="col-span-2">
            <dt className="text-sage-500">Location</dt>
            <dd className="text-forest-900">{workorder.location}</dd>
          </div>
        )}
        {workorder.notes && (
          <div className="col-span-2">
            <dt className="text-sage-500">Notes</dt>
            <dd className="text-forest-900">{workorder.notes}</dd>
          </div>
        )}
      </dl>
      <div className="flex gap-2 mt-4 pt-4 border-t border-sage-200">
        <Button variant="primary" onClick={() => console.log('Edit:', workorder.id)}>Edit</Button>
        <Button variant="ghost" onClick={() => console.log('Complete:', workorder.id)}>Mark Complete</Button>
      </div>
    </Card>
  );
}
